export class ContactRequestError extends Error {
    constructor(
        message: string,
        public status?: number,
        public code?: string,
        public retryAt?: number
    ) {
        super(message);
    }
}

/** Bound the entire operation, including response parsing, and cancel it on unmount. */
export async function withContactDeadline<T>(
    operation: (signal: AbortSignal) => Promise<T>,
    timeout: number,
    parentSignal: AbortSignal,
    timeoutMessage: string
): Promise<T> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let cancel: () => void = () => {};
    const cancelled = new Promise<never>((_resolve, reject) => {
        cancel = () => {
            controller.abort();
            reject(new DOMException('Request cancelled', 'AbortError'));
        };
        parentSignal.addEventListener('abort', cancel, { once: true });
        timer = setTimeout(() => {
            controller.abort();
            reject(new ContactRequestError(timeoutMessage));
        }, timeout);
    });
    try {
        if (parentSignal.aborted) cancel();
        return await Promise.race([cancelled, operation(controller.signal)]);
    } finally {
        clearTimeout(timer!);
        parentSignal.removeEventListener('abort', cancel);
    }
}

export function requestContactJSON(
    url: string,
    options: RequestInit,
    signal: AbortSignal,
    submission = false
): Promise<Record<string, unknown>> {
    return withContactDeadline(
        async requestSignal => {
            let response: Response;
            try {
                response = await fetch(url, { ...options, credentials: 'include', signal: requestSignal });
            } catch (error) {
                if (requestSignal.aborted) throw error;
                throw new ContactRequestError(
                    submission
                        ? 'Connection lost. Delivery could not be confirmed. Your draft is saved; retrying may send a duplicate.'
                        : 'Cannot connect. Please check your connection and try again.'
                );
            }
            const retryAfter = response.headers.get('Retry-After');
            const seconds = retryAfter === null ? NaN : Number(retryAfter);
            const retryAt =
                retryAfter === null
                    ? undefined
                    : Number.isFinite(seconds)
                      ? Date.now() + Math.max(0, seconds) * 1000
                      : Date.parse(retryAfter);
            let result: unknown;
            try {
                result = await response.json();
            } catch {
                throw new ContactRequestError(
                    'The server returned an unreadable response. Please try again.',
                    response.status,
                    undefined,
                    retryAt
                );
            }
            if (!result || typeof result !== 'object' || Array.isArray(result)) {
                throw new ContactRequestError(
                    'The server returned an unexpected response. Please try again.',
                    response.status
                );
            }
            const data = result as Record<string, unknown>;
            if (!response.ok) {
                throw new ContactRequestError(
                    typeof data.message === 'string'
                        ? data.message
                        : `Request failed (${response.status}). Please try again.`,
                    response.status,
                    typeof data.code === 'string' ? data.code : undefined,
                    retryAt
                );
            }
            return data;
        },
        submission ? 120000 : 10000,
        signal,
        submission
            ? 'Sending timed out. Delivery could not be confirmed. Your draft is saved; retrying may send a duplicate.'
            : 'The request timed out. Please try again.'
    );
}
